import React, { useState, useMemo } from 'react';
import { useDemo } from '../../state/DemoContext';
import { filterListings, sortListings, formatCurrency } from '../../utils/helpers';
import { ASSET_TYPES, LIEN_POSITIONS, LISTING_STATUS } from '../../utils/constants';
import ListingCard from '../molecules/ListingCard';
import './marketplace.css';

const Marketplace = () => {
  const { state, actions } = useDemo();
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Get filtered and sorted listings
  const filteredListings = useMemo(() => {
    let filtered = filterListings(state.listings, state.marketplaceFilters);
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(listing => 
        listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return sortListings(filtered, sortBy, sortOrder);
  }, [state.listings, state.marketplaceFilters, searchTerm, sortBy, sortOrder]);

  // Get unique locations for filter
  const locations = useMemo(() => {
    const uniqueLocations = [...new Set(state.listings.map(l => l.location.split(',')[1]?.trim()).filter(Boolean))];
    return uniqueLocations.sort();
  }, [state.listings]);

  const handleFilterChange = (filterType, value) => {
    actions.updateMarketplaceFilters({ [filterType]: value });
  };

  const clearFilters = () => {
    actions.updateMarketplaceFilters({
      assetType: 'all',
      ltvRange: [0, 1],
      location: 'all',
      lienPosition: 'all',
      status: 'all'
    });
    setSearchTerm('');
  };

  const getPropertyForListing = (listing) => {
    return state.properties.find(p => p.id === listing.assetId);
  };

  const getListingStats = () => {
    const total = state.listings.length;
    const open = state.listings.filter(l => l.status === 'Open').length;
    const matched = state.listings.filter(l => l.status === 'Matched').length;
    const totalValue = state.listings.reduce((sum, l) => sum + l.requestedAmount, 0);
    
    return { total, open, matched, totalValue };
  };

  const stats = getListingStats();

  return (
    <div className="jo-xyz-demo-marketplace-page">
      <div className="jo-xyz-demo-marketplace-page__header">
        <h1>Marketplace</h1>
        <p className="jo-xyz-demo-marketplace-page__subtitle">
          Browse and invest in commercial real estate lending opportunities
        </p>
      </div>

      {/* Stats Overview */}
      <div className="jo-xyz-demo-marketplace-page__stats">
        <div className="jo-xyz-demo-grid jo-xyz-demo-grid--4">
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{stats.total}</div>
              <div className="jo-xyz-demo-stat-card__label">Total Listings</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{stats.open}</div>
              <div className="jo-xyz-demo-stat-card__label">Open Opportunities</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{stats.matched}</div>
              <div className="jo-xyz-demo-stat-card__label">Fully Funded</div>
            </div>
          </div>
          <div className="jo-xyz-demo-stat-card">
            <div className="jo-xyz-demo-stat-card__content">
              <div className="jo-xyz-demo-stat-card__value">{formatCurrency(stats.totalValue)}</div>
              <div className="jo-xyz-demo-stat-card__label">Total Value</div>
            </div>
          </div>
        </div>
      </div>

      <div className="jo-xyz-demo-marketplace-page__content">
        {/* Filters Sidebar */}
        <div className="jo-xyz-demo-marketplace-page__filters">
          <div className="jo-xyz-demo-card">
            <div className="jo-xyz-demo-card__header">
              <h3 className="jo-xyz-demo-card__title">Filters</h3>
              <button
                className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--sm"
                onClick={clearFilters}
              >
                Clear All
              </button>
            </div>

            <div className="jo-xyz-demo-filters-content">
              {/* Search */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">Search</label>
                <input
                  type="text"
                  className="jo-xyz-demo-input"
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Asset Type */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">Asset Type</label>
                <select
                  className="jo-xyz-demo-select jo-xyz-demo-input"
                  value={state.marketplaceFilters.assetType}
                  onChange={(e) => handleFilterChange('assetType', e.target.value)}
                >
                  <option value="all">All Types</option>
                  {ASSET_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* LTV Range */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">LTV Range</label>
                <div className="jo-xyz-demo-ltv-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={state.marketplaceFilters.ltvRange[1]}
                    onChange={(e) => handleFilterChange('ltvRange', [0, parseFloat(e.target.value)])}
                    className="jo-xyz-demo-ltv-slider"
                  />
                  <div className="jo-xyz-demo-ltv-range__labels">
                    <span>0%</span>
                    <span>{Math.round(state.marketplaceFilters.ltvRange[1] * 100)}%</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">Location</label>
                <select
                  className="jo-xyz-demo-select jo-xyz-demo-input"
                  value={state.marketplaceFilters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              {/* Lien Position */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">Lien Position</label>
                <select
                  className="jo-xyz-demo-select jo-xyz-demo-input"
                  value={state.marketplaceFilters.lienPosition}
                  onChange={(e) => handleFilterChange('lienPosition', e.target.value)}
                >
                  <option value="all">All Positions</option>
                  {LIEN_POSITIONS.map(position => (
                    <option key={position} value={position}>
                      {position === 'FirstLien' ? 'First Lien' :
                       position === 'SecondLien' ? 'Second Lien' :
                       position}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="jo-xyz-demo-filter-group">
                <label className="jo-xyz-demo-filter-label">Status</label>
                <select
                  className="jo-xyz-demo-select jo-xyz-demo-input"
                  value={state.marketplaceFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  {LISTING_STATUS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        <div className="jo-xyz-demo-marketplace-page__listings">
          <div className="jo-xyz-demo-listings-header">
            <div className="jo-xyz-demo-listings-header__left">
              <h2>Available Listings</h2>
              <span className="jo-xyz-demo-listings-count">
                {filteredListings.length} listing{filteredListings.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="jo-xyz-demo-listings-header__right">
              <div className="jo-xyz-demo-sort-controls">
                <label className="jo-xyz-demo-sort-label">Sort by:</label>
                <select
                  className="jo-xyz-demo-select jo-xyz-demo-input"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="created">Date Created</option>
                  <option value="amount">Amount</option>
                  <option value="ltv">LTV</option>
                  <option value="expires">Expiry Date</option>
                </select>
                <button
                  className="jo-xyz-demo-btn jo-xyz-demo-btn--secondary jo-xyz-demo-btn--sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>

          {filteredListings.length === 0 ? (
            <div className="jo-xyz-demo-listings-empty">
              <div className="jo-xyz-demo-card">
                <div className="jo-xyz-demo-empty-state">
                  <div className="jo-xyz-demo-empty-state__icon">🔍</div>
                  <h3>No Listings Found</h3>
                  <p>Try adjusting your filters or search terms to find more opportunities.</p>
                  <button
                    className="jo-xyz-demo-btn jo-xyz-demo-btn--primary"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="jo-xyz-demo-listings-grid">
              {filteredListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  property={getPropertyForListing(listing)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
